import { Children, isValidElement, useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { backFromState } from "@/lib/back-target";
import {
  ArrowLeft, Mail, Phone, MapPin, Trash2, ExternalLink, BanIcon, User, Cake, Eye, EyeOff, Merge, Flame,
  ShoppingCart,
} from "lucide-react";
import { Badge, Button, Skeleton, useConfirm, useToast } from "@/components/ui";
import { cn } from "@/lib/utils";
import { BookButton } from "@/features/scheduling/BookButton";
import { ExpandableMessages } from "@/features/inbox/ExpandableMessages";
import { ContactAvatarUpload } from "@/features/contacts/AvatarUpload";
import { MergeDialog } from "@/features/contacts/MergeDialog";
import { InlineEditField, InlineEditSelect, InlineEditCheckbox } from "@/features/contacts/InlineEdit";
import {
  AppointmentsPanel, AutomationsPanel, ConsentPanel, NotesPanel, OrderHistoryPanel, PaymentsPanel, TagsPanel, TasksPanel, TimelinePanel,
} from "@/features/contacts/InfoPanels";
import { InsurancePanel } from "@/features/contacts/InsurancePanel";
import {
  MedicalPanel, ChartsPanel, CardGalleryPanel, DocumentsPanel, ClientFormsPanel,
  MedicalDirectorPanel,
  ClientStatStrip, AlertsPanel, PortalLinkPanel, CardOnFilePanel, ClientFilePanel,
  VouchersPanel, AccountCreditPanel, GiftCardsPanel,
  MembershipPanel, PackageHistoryPanel,
} from "@/features/contacts/MedspaPanels";
import { api, ApiError } from "@/lib/api";
import { fullName, formatPhone, formatDate, formatDateTime } from "@/lib/format";
import type { Contact, CustomField } from "@/lib/types";
import { canAny, useMe, type Capability } from "@/lib/me";

/** Her words, not a sales funnel's: somebody who has booked is a client. */
const LIFECYCLE_OPTIONS = [
  { value: "lead",     label: "Lead" },
  { value: "contact",  label: "Enquiry" },
  { value: "customer", label: "Client" },
  { value: "churned",  label: "Lapsed" },
];

const SOCIAL_NETWORKS: { key: string; label: string; placeholder: string }[] = [
  { key: "facebook",  label: "Facebook",   placeholder: "facebook.com/handle" },
  { key: "instagram", label: "Instagram",  placeholder: "@handle" },
  { key: "linkedin",  label: "LinkedIn",   placeholder: "linkedin.com/in/handle" },
  { key: "twitter",   label: "Twitter / X", placeholder: "@handle" },
  { key: "tiktok",    label: "TikTok",     placeholder: "@handle" },
  { key: "youtube",   label: "YouTube",    placeholder: "youtube.com/@handle" },
  { key: "whatsapp",  label: "WhatsApp",   placeholder: "+1…" },
  { key: "telegram",  label: "Telegram",   placeholder: "@handle" },
  { key: "website",   label: "Website",    placeholder: "https://…" },
];

const CHART_TABS = [
  { key: "overview",    label: "Overview" },
  { key: "messages",    label: "Messages" },
  { key: "charts",      label: "Chart notes" },
  { key: "labs",        label: "Labs" },
  { key: "forms",       label: "Forms" },
  { key: "medical",     label: "Medical info" },
  /* "We will need tabs in the individual chart: 1- Gallery for all photo in
     and insurance cards. 2-Insurance Tracking" */
  { key: "gallery",     label: "Gallery" },
  { key: "insurance",   label: "Insurance" },
  { key: "history",     label: "History" },
  { key: "wallet",      label: "Wallet" },
  { key: "membership",  label: "Membership" },
  { key: "packages",    label: "Packages" },
] as const;

type ChartTab = (typeof CHART_TABS)[number]["key"];

/** Tabs that belong to another section; the rest are part of the file. */
const TAB_NEEDS: Partial<Record<ChartTab, Capability>> = {
  messages: "messages",
  charts: "clinical",
  labs: "clinical",
  medical: "clinical",
  gallery: "clinical",
  insurance: "clinical",
};

export function ContactDetailPage() {
  // ?tab=lab_results makes any part of the chart linkable and bookmarkable —
  // and keeps the tab in the URL, so a browser Back lands where she was.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const me = useMe();
  const tabs = CHART_TABS.filter((t) => canAny(me, TAB_NEEDS[t.key]));
  const tab: ChartTab = tabs.some((t) => t.key === urlTab)
    ? (urlTab as ChartTab)
    : "overview";
  const clinicalOpen = canAny(me, "clinical");
  const setTab = (next: ChartTab) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "overview") p.delete("tab");
        else p.set("tab", next);
        return p;
      },
      { replace: true },
    );
  };
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const back = backFromState(location.state);
  const toast = useToast();
  const confirm = useConfirm();
  const [contact, setContact] = useState<Contact | null>(null);
  // Custom fields list is workspace-wide so we fetch it once here for the
  // inline editor on the left rail. The right-rail panels (timeline,
  // tasks, payments, etc.) own their own data fetching via <InfoPanels>.
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [mergeOpen, setMergeOpen] = useState(false);
  // "Hide empty fields" toggle persists across contacts via localStorage.
  const [hideEmpty, setHideEmpty] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem("crm.contact.hideEmpty");
    return saved === null ? true : saved === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("crm.contact.hideEmpty", hideEmpty ? "1" : "0");
  }, [hideEmpty]);

  /**
   * Initial / silent reload of every panel on this page. `silent=true`
   * skips the loading spinner and error toasts so background polls don't
   * blink the rails when nothing has changed. We always replace the
   * canonical contact row but preserve the locally-rendered tag list (the
   * /contacts/:id endpoint doesn't include the joined tags) — tags are
   * managed via their own optimistic addTag/removeTag flow.
   */
  const load = async (silent = false) => {
    if (!id) return;
    try {
      const [c, cf] = await Promise.all([
        api.get<{ contact: Contact }>(`/contacts/${id}`),
        api.get<{ fields: CustomField[] }>("/custom-fields"),
      ]);
      setContact(c.contact);
      setCustomFields(cf.fields);
    } catch (err) {
      if (!silent) {
        toast.error(err instanceof ApiError ? err.message : "Failed to load contact");
        if (err instanceof ApiError && err.status === 404) navigate(back.to);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  // Background poll so AI-driven updates (or another tab) propagate without
  // a manual refresh. The right-rail panels poll their own data internally.
  useEffect(() => {
    if (!id) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => { if (timer) return; timer = setInterval(() => { void load(true); }, 15000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.hidden) stop(); else start(); };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [id]);

  /** Optimistic patch on the contact row itself. Tags are managed by TagsPanel. */
  const patch = async (delta: Partial<Contact>) => {
    if (!contact || !id) return;
    const prev = contact;
    setContact({ ...contact, ...delta } as Contact);
    try {
      const r = await api.patch<{ contact: Contact }>(`/contacts/${id}`, delta);
      setContact({ ...r.contact, tags: prev.tags });
    } catch (err) {
      setContact(prev);
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
      throw err;
    }
  };

  const onDelete = async () => {
    if (!id) return;
    const fullName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") : null;
    if (!(await confirm({
      title: "Delete this contact?",
      description: (
        <>
          {fullName ? <strong className="text-zinc-100">{fullName}</strong> : "This contact"} and
          all their messages, tasks, notes, and activity will be permanently deleted.
          This can't be undone.
        </>
      ),
      confirmLabel: "Delete contact",
      danger: true,
    }))) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contact deleted");
      navigate(back.to);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col p-3 sm:p-4 lg:p-6 space-y-4 overflow-hidden">
        <Skeleton className="h-8 w-48" />
        <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-12 min-h-0">
          <div className="space-y-3 lg:col-span-3"><Skeleton className="h-44" /><Skeleton className="h-32" /><Skeleton className="h-24" /></div>
          <Skeleton className="lg:col-span-6" />
          <div className="space-y-3 lg:col-span-3"><Skeleton className="h-32" /><Skeleton className="h-24" /></div>
        </div>
      </div>
    );
  }
  if (!contact) return null;

  return (
    // h-full + overflow-y-auto on mobile so the stacked rails scroll
    // naturally inside <main> (which is overflow-hidden in full-bleed
    // mode). On lg+ we lock scrolling here and let each column scroll
    // independently via `lg:overflow-y-auto` on the asides.
    <div className="flex h-full flex-col overflow-y-auto lg:overflow-hidden">
      {/* Slim top bar — back + delete only. The hide-empty toggle moved to
          its own card on the left rail. */}
      <div className="flex items-center gap-3 border-b border-zinc-800/70 px-3 sm:px-4 lg:px-6 py-2 shrink-0">
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate(back.to)}>
          <span className="hidden sm:inline">{back.label}</span>
        </Button>
        <div className="flex-1" />
        {/* Book the person whose record you're looking at, without going to
            the calendar and searching for them again. */}
        <BookButton contact={contact} label="Book" />
        {/*
          "I need a cart for each client … if they walk in for a pkg of
           injections there is no way to wring up without a schedule visit"
          There always was - the till takes a client without a visit - but the
          only way in was from a booking, so a walk-in buying a package looked
          impossible. This is that way in, with the client already on the
          ticket.
        */}
        {canAny(me, ["checkout"]) && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ShoppingCart className="h-4 w-4" />}
            onClick={() => navigate(`/checkout?contact_id=${contact.id}`)}
          >
            <span className="hidden sm:inline">Check out</span>
          </Button>
        )}
        <Button variant="ghost" size="sm" leftIcon={<Merge className="h-4 w-4" />} onClick={() => setMergeOpen(true)}>
          <span className="hidden sm:inline">Merge duplicate</span>
        </Button>
        <Button variant="ghost" size="sm" leftIcon={<Trash2 className="h-4 w-4 text-rose-400" />} onClick={onDelete} className="text-rose-300 hover:bg-rose-500/10">
          <span className="hidden sm:inline">Delete</span>
        </Button>
      </div>

      {/* Three-column body on lg+. On mobile the columns stack and scroll
          with the page (no nested scrolling); on lg+ each column manages
          its own vertical scroll and the window itself stays fixed-height. */}
      <div className="grid flex-1 grid-cols-1 gap-3 px-3 sm:px-4 lg:grid-cols-12 lg:gap-4 lg:px-6 py-3 lg:min-h-0">
        {/* Left rail */}
        <aside className="space-y-3 lg:overflow-y-auto lg:pr-1 lg:col-span-3">
          {/* Profile (name + clickable avatar for upload) */}
          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5 text-center">
            <ContactAvatarUpload
              contact={contact}
              onSaved={(c) => setContact({ ...c, tags: contact.tags })}
            />
            <h1 className="mt-3 text-base font-semibold text-zinc-100">
              {fullName(contact) || <span className="italic text-zinc-500">Unnamed</span>}
              {contact.tags.some((t) => t.emoji) && (
                <span className="ml-1.5 align-middle text-base" title={contact.tags.filter((t) => t.emoji).map((t) => t.name).join(", ")}>
                  {contact.tags.map((t) => t.emoji).filter(Boolean).join("")}
                </span>
              )}
            </h1>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              <LeadScoreBadge score={contact.lead_score} />
              {contact.do_not_contact && (
                <Badge color="rose" size="sm"><BanIcon className="h-3 w-3 mr-1" /> Do not contact</Badge>
              )}
            </div>
          </div>

          {/* Hide-empty-fields toggle in its own card so it's visible at a glance */}
          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-3">
            <label className="flex cursor-pointer items-center gap-2.5 text-xs text-zinc-300">
              <input
                type="checkbox"
                checked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900 text-indigo-500"
              />
              {hideEmpty ? <EyeOff className="h-3.5 w-3.5 text-zinc-400" /> : <Eye className="h-3.5 w-3.5 text-zinc-400" />}
              <span className="flex-1">Hide empty fields</span>
              <span className="text-[10px] text-zinc-500">not the basics</span>
            </label>
          </div>

          {/*
            * Identity, always shown - empty or not.
            *
            *   "sometimes we create patients without all the info we need for
            *    various reasons. I created this one here and once I get her
            *    email I have no way to add it in. Can this show automatically
            *    even if the field is left open"
            *
            * Hiding an empty field also hides the only way to fill it, and
            * these five are exactly the ones a client file gets opened
            * without. Everything below still answers to the toggle.
            */}
          <Card title="Identity">
            <Row icon={User} label="First name">
              <InlineEditField value={contact.first_name} onSave={(v) => patch({ first_name: v })} label="First name" />
            </Row>
            <Row icon={User} label="Last name">
              <InlineEditField value={contact.last_name} onSave={(v) => patch({ last_name: v })} label="Last name" />
            </Row>
            <Row icon={Mail} label="Email">
              <InlineEditField value={contact.email} onSave={(v) => patch({ email: v })} kind="email" label="Email" />
            </Row>
            <Row icon={Phone} label="Phone">
              <InlineEditField value={contact.phone} onSave={(v) => patch({ phone: v })} kind="tel" label="Phone" formatDisplay={(v) => formatPhone(v)} />
            </Row>
            <Row icon={Cake} label="Birthday">
              <InlineEditField value={contact.date_of_birth} onSave={(v) => patch({ date_of_birth: v })} kind="date" label="Date of birth" formatDisplay={(v) => formatDate(v)} />
            </Row>
          </Card>

          {/* Lifecycle — stage always shown (it's never empty) */}
          <Card title="Lifecycle">
            <Row label="Stage">
              <InlineEditSelect value={contact.lifecycle_stage ?? "lead"} options={LIFECYCLE_OPTIONS} onSave={(v) => patch({ lifecycle_stage: v })} label="Lifecycle stage" />
            </Row>
            <Row label="Source" hidden={hideEmpty && !contact.source}>
              <InlineEditField value={contact.source} onSave={(v) => patch({ source: v })} label="Source" placeholder="manual, form, import…" />
            </Row>
            {/* A Boulevard regular isn't new just because this system hasn't
                seen a visit yet. Ticked, they can book any treatment online
                and the front desk drops the new-client star. A completed
                visit here counts on its own. */}
            <Row label="Visited before">
              <InlineEditCheckbox
                value={contact.returning_client === true}
                onSave={(v) => patch({ returning_client: v })}
                label="Been here before (e.g. in Boulevard)"
              />
            </Row>
            {/* "Her date of first contact is Oct 13 - so she is not the
                third." Advertising dates an enquiry from the day the record
                was made, which is right to the minute for anybody who books
                online and wrong for anybody the desk adds later. This is
                where she says otherwise, and Advertising believes her. */}
            <Row label="First got in touch">
              <InlineEditField
                value={contact.first_contact_at ?? null}
                onSave={(v) => patch({ first_contact_at: v || null })}
                kind="date"
                label="First got in touch"
                formatDisplay={(v) => formatDate(v)}
                placeholder="Day it was added"
              />
            </Row>
            {/* The legacy "Do not contact" checkbox lived here. The canonical
             *  master kill-switch is now the "Do not disturb / Block all
             *  channels" toggle in the Communication-channels card, backed
             *  by contact_consent (channel='all'). The DB still keeps
             *  contacts.do_not_contact in sync for back-compat with older
             *  send paths, so toggling DND there flips both. */}
          </Card>

          {/* Address */}
          <Card title="Address">
            <Row icon={MapPin} label="Street" hidden={hideEmpty && !contact.address_line1}>
              <InlineEditField value={contact.address_line1} onSave={(v) => patch({ address_line1: v })} label="Street" />
            </Row>
            <Row label="City" hidden={hideEmpty && !contact.city}>
              <InlineEditField value={contact.city} onSave={(v) => patch({ city: v })} label="City" />
            </Row>
            <Row label="State / Region" hidden={hideEmpty && !contact.state}>
              <InlineEditField value={contact.state} onSave={(v) => patch({ state: v })} label="State / Region" />
            </Row>
            <Row label="Postal code" hidden={hideEmpty && !contact.postal_code}>
              <InlineEditField value={contact.postal_code} onSave={(v) => patch({ postal_code: v })} label="Postal code" />
            </Row>
            <Row label="Country" hidden={hideEmpty && !contact.country}>
              <InlineEditField value={contact.country} onSave={(v) => patch({ country: v })} label="Country" />
            </Row>
          </Card>

          {/* Social profiles */}
          <Card title="Social profiles">
            {SOCIAL_NETWORKS.map(({ key, label, placeholder }) => {
              const value = contact.socials[key] ?? "";
              const isUrl = /^https?:/.test(value);
              return (
                <Row key={key} label={label} hidden={hideEmpty && !value}>
                  <div className="flex items-center gap-1">
                    <div className="flex-1 min-w-0">
                      <InlineEditField
                        value={value}
                        onSave={(v) => patch({ socials: { ...contact.socials, [key]: v ?? "" } })}
                        kind="url"
                        label={label}
                        placeholder={placeholder}
                      />
                    </div>
                    {isUrl && (
                      <a href={value} target="_blank" rel="noreferrer" className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-indigo-300" aria-label={`Open ${label}`}>
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </Row>
              );
            })}
          </Card>

          {/* Custom fields */}
          {customFields.length > 0 && (
            <Card title="Custom fields">
              {customFields.map((f) => {
                const v = contact.custom_fields[f.key];
                return (
                  // Never hidden by "hide empty": a custom field exists to be
                  // filled in (referral source is the reason she asked for
                  // them), and one you can't see is one nobody ever sets.
                  <Row key={f.key} label={f.label}>
                    <CustomFieldInlineEdit
                      field={f}
                      value={v}
                      onSave={(next) => patch({ custom_fields: { ...contact.custom_fields, [f.key]: next } })}
                    />
                  </Row>
                );
              })}
            </Card>
          )}

          {/* "remove Tags over to under identity (smaller box but need color)
              possible to show what tags and add a stop to add or delete tags
              drop down box" — they describe the person, so they belong with
              the rest of who the person is rather than above their visits. */}
          <TagsPanel contact={contact} onContactChange={(c) => setContact(c)} compact />

          {/* Per-channel do-not-disturb sits at the bottom of the left rail,
              alongside the contact's identity fields, since it's a property
              *of* the contact rather than activity. */}
          <ConsentPanel contact={contact} />
        </aside>

        {/* The client record, as tabs.
            Her words: "inside patient chart we will need separated tabs for
            Lab Scripts / Lab Results / Intake-renewal Forms / Medical
            Information / Messages / History / Wallet / Membership /
            Packages." Everything below already existed as panels stacked in
            one long column; the tabs are what make it findable. */}
        <section className="lg:col-span-9 min-h-112 lg:min-h-0 flex flex-col gap-3">
          <ClientStatStrip contactId={contact.id} />

          <div className="-mx-1 flex gap-1 overflow-x-auto border-b border-zinc-800/70 px-1 pb-px">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  "shrink-0 rounded-t-md border-b-2 px-3 py-2 text-sm whitespace-nowrap",
                  tab === t.key
                    ? "border-indigo-500 font-semibold text-zinc-100"
                    : "border-transparent text-zinc-500 hover:text-zinc-200",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* "when in patient chart the tab at the bottom in messages and
              forms, medical info it gets in the way most with messages and
              the optional buttons to polish" — the Front Desk button is fixed
              to this corner on every screen, and she wants it to stay there.
              The chart is the one page whose panels run right down into it:
              in Messages it sat on top of Polish and Send. So the column
              keeps the button's footprint clear instead of the button moving.
              76px = 44 tall + 20 from the bottom + a bit of air. */}
          <div className="min-h-0 flex-1 space-y-3 pb-[76px] lg:overflow-y-auto lg:pr-1">
            {tab === "overview" && (
              <>
                {/* "In the overview - No visit notes, no forms no everything.
                    Start at the top with scheduled appointments then
                    Scheduling alerts."
                    The clinical file, the card on file and the tasks/notes
                    drawer all moved out: the file is under Chart notes and
                    Forms, the card is in the Wallet where she keeps it, and
                    the overview is what is booked and what to know first. */}
                {/* "in that area when we click on the appointment it's can we see if
    any which forms were sent out?" — the paperwork for each upcoming
    visit, with a resend on anything still outstanding. */}
                <AppointmentsPanel contactId={contact.id} when="upcoming" title="Coming up" max={10} withForms />
                {/* "can we have a section for alerts under coming up in the
                    overview page … Then we need also Follow up Lab Work /
                    Follow up Consultations … They do show in the appointment
                    box but need them here as well" — one list, right under
                    Coming up, holding the scheduling alert that was already
                    here, any other alert, and the two dated follow-ups. */}
                <AlertsPanel
                  contactId={contact.id}
                  schedulingAlert={contact.scheduling_alert ?? null}
                  onSchedulingAlertSaved={(v) => setContact((c) => (c ? { ...c, scheduling_alert: v } : c))}
                />
                <AppointmentsPanel contactId={contact.id} title="Recent visits" />
                <PortalLinkPanel contactId={contact.id} />
              </>
            )}
            {tab === "messages" && <ExpandableMessages contact={contact} />}
            {tab === "labs" && (
              <>
                {/* One place for everything lab-shaped — her exact list:
                    scripts, results, recommendation letters. Each section
                    takes uploads (PDFs, scans) with a note per file. */}
                <DocumentsPanel contactId={contact.id} only="lab_script" title="Lab scripts" />
                <DocumentsPanel contactId={contact.id} only="lab_result" title="Lab results" />
                <DocumentsPanel contactId={contact.id} only="recommendation" title="Recommendation letters" />
                {/* "also can you add a blvd section to the labs please those
                    will need the upload" — somewhere to put the old lab work
                    as it comes across, without sorting it first. */}
                <DocumentsPanel contactId={contact.id} only="boulevard_lab" title="From Boulevard" />
              </>
            )}
            {tab === "forms" && (
              /* "So that tab in the chart Forms will have there sub titles:
                  Forms / Medical Director / Paperwork on file" */
              <>
                <ClientFormsPanel contactId={contact.id} />
                <MedicalDirectorPanel contactId={contact.id} />
                {/* Paperwork that arrived as a file (emailed in, or scanned)
                    rather than as one of our own forms. */}
                <DocumentsPanel contactId={contact.id} only="form" title="Paperwork on file" />
              </>
            )}
            {tab === "medical" && (
              <>
                <MedicalPanel contactId={contact.id} />
                {/* "I need to be able to upload medication or supplement list
                    in medical info under medications" — the sheet they bring
                    in, or a photo of the bottles, sitting directly under the
                    medications she has just read. */}
                <DocumentsPanel contactId={contact.id} only="medication_list" title="Medication & supplement list" />
                {/* The photo ID and insurance cards have their own Gallery
                    tab now, which is where she asked for them. */}
                <DocumentsPanel contactId={contact.id} only="medical_info" title="Medical information on file" />
              </>
            )}
            {tab === "charts" && (
              <>
                <ChartsPanel contactId={contact.id} />
                {/* "How do I get the pre-noted nursing and PA notes in the
                    system?" Their Boulevard notes, as files on the chart. */}
                <DocumentsPanel contactId={contact.id} only="prior_notes" title="Earlier nursing & provider notes (from Boulevard)" />
              </>
            )}
            {tab === "gallery" && (
              <>
                {/* "a Gallery for all photo in and insurance cards" — the
                    pictures she used to keep in Boulevard's gallery, in one
                    place rather than spread across the medical tab. */}
                <CardGalleryPanel contactId={contact.id} />
                <DocumentsPanel contactId={contact.id} only="photo" title="Photos" />
              </>
            )}
            {tab === "insurance" && <InsurancePanel contactId={contact.id} />}
            {tab === "history" && (
              <>
                {/* Her spec for History: visits, purchases, memberships,
                    packages — and NO message chatter (that's the Messages
                    tab's job). */}
                {/* Newest at the top, and the paperwork for each visit
                    hanging off it — "is it possible to have a link to the
                    forms needed for that appointment". */}
                {/* "Remove payments. Remove memberships and credits." Both
                    live in the Wallet, which is where she keeps money; this
                    tab is what they have had and what they have bought. */}
                <AppointmentsPanel contactId={contact.id} max={50} title="Appointment history" withForms />
                <OrderHistoryPanel contactId={contact.id} title="Product history" kinds={["product"]} />
                <PackageHistoryPanel contactId={contact.id} />
                <TimelinePanel contactId={contact.id} opsOnly />
              </>
            )}
            {tab === "wallet" && (
              <>
                {/* Her order, her words: "So blue titles are: Card on File /
                    Vouchers / Account Credits / Gift Cards / Payments. In that
                    order 😊" */}
                <CardOnFilePanel contactId={contact.id} />
                <VouchersPanel contactId={contact.id} />
                <AccountCreditPanel contactId={contact.id} />
                <GiftCardsPanel contactId={contact.id} />
                <PaymentsPanel contactId={contact.id} />
              </>
            )}
            {tab === "membership" && <MembershipPanel contactId={contact.id} />}
            {tab === "packages" && <PackageHistoryPanel contactId={contact.id} />}
          </div>
        </section>
      </div>

      {/* Merge a duplicate contact INTO this one. The current contact is the
          survivor; on success we silently reload every rail so the merged-in
          messages, tasks, notes, and activity appear. */}
      <MergeDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        winner={contact}
        onMerged={() => { void load(true); }}
      />
    </div>
  );
}

/**
 * Small engagement-heat indicator. lead_score is 0–100; we bucket it into
 * cold (zinc), warm (amber), and hot (emerald) so the color reads at a
 * glance without the user having to interpret the number.
 */
function LeadScoreBadge({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, Math.round(score ?? 0)));
  const color = clamped >= 67 ? "emerald" : clamped >= 34 ? "amber" : "zinc";
  return (
    <Badge color={color} size="sm">
      <Flame className="h-3 w-3 mr-1" /> Lead score {clamped}
    </Badge>
  );
}

// ─── Building blocks for the rails ────────────────────────────────────────
/**
 * Card collapses entirely when every Row inside is hidden. We can't just
 * `filter(Boolean)` the children — Row elements are always truthy regardless
 * of their `hidden` prop. Instead we walk the children, peek at each one's
 * `hidden` prop, and treat the card as empty when every Row passed
 * `hidden=true`.
 */
function Card({ title, children }: { title: string; children: ReactNode }) {
  const arr = Children.toArray(children);
  const anyVisible = arr.some((child) => {
    if (isValidElement(child)) {
      const props = child.props as { hidden?: boolean };
      return !props.hidden;
    }
    return Boolean(child);
  });
  if (!anyVisible) return null;
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
      {/* "I think that life cycle title should be in the blue like all our
          others as well as identity custom fields should carry throughout the
          software" — the same heading as every panel on the right. */}
      <h3 className="mb-3 text-sm font-bold tracking-wide text-sky-300">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

/**
 * One field row with the label stacked ABOVE the editor (per the requested
 * layout). Setting `hidden` returns null, which Card detects via the
 * children walk above to collapse the whole section if nothing is showing.
 */
function Row({ icon: Icon, label, children, hidden }: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  children: ReactNode;
  hidden?: boolean;
}) {
  if (hidden) return null;
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function CustomFieldInlineEdit({ field, value, onSave }: {
  field: CustomField;
  value: unknown;
  onSave: (next: unknown) => Promise<void>;
}) {
  switch (field.field_type) {
    case "boolean":
      return (
        <InlineEditCheckbox
          value={!!value}
          onSave={(v) => onSave(v)}
          label={value ? "Yes" : "No"}
        />
      );
    case "longtext":
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v)}
          kind="longtext"
          label={field.label}
        />
      );
    case "number":
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v == null ? null : Number(v))}
          kind="number"
          label={field.label}
        />
      );
    case "date":
      return (
        <InlineEditField
          value={value == null ? null : String(value).slice(0, 10)}
          onSave={(v) => onSave(v)}
          kind="date"
          label={field.label}
          formatDisplay={(v) => formatDate(v)}
        />
      );
    case "datetime":
      return (
        <InlineEditField
          value={value == null ? null : String(value).slice(0, 16)}
          onSave={(v) => onSave(v)}
          kind="datetime"
          label={field.label}
          formatDisplay={(v) => formatDateTime(v)}
        />
      );
    case "url":
    case "email":
    case "phone":
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v)}
          kind={field.field_type === "phone" ? "tel" : field.field_type}
          label={field.label}
        />
      );
    case "dropdown":
      return (
        <InlineEditSelect
          value={value == null ? "" : String(value)}
          options={[
            { value: "", label: "—" },
            ...field.options.map((o) => ({ value: o, label: o })),
          ]}
          onSave={(v) => onSave(v === "" ? null : v)}
          label={field.label}
        />
      );
    case "multiselect": {
      const current = Array.isArray(value) ? (value as string[]).join(", ") : "";
      return (
        <InlineEditField
          value={current}
          onSave={(v) => onSave(v == null ? [] : v.split(",").map((s) => s.trim()).filter(Boolean))}
          label={field.label}
          placeholder="comma-separated"
        />
      );
    }
    default:
      return (
        <InlineEditField
          value={value == null ? null : String(value)}
          onSave={(v) => onSave(v)}
          label={field.label}
        />
      );
  }
}

